import type zhCN from './zh-CN';

/**
 * ★ 用 `satisfies typeof zhCN` 而不是 `as`：中文包加一个键、英文包漏一个键，
 *   typecheck 当场失败（线上表现是英文页面显示 raw key，而这种错误在浏览器里很难被发现）。
 */
export default {
  nav: {
    home: 'Home',
    templates: 'Templates',
    login: 'Sign in',
    logout: 'Sign out',
    account: 'Account',
    help: 'Help center',
    terms: 'Terms',
    privacy: 'Privacy',
    refund: 'Refunds',
  },
  home: {
    title: 'Buy a template, own the whole site',
    subtitle:
      'Every site is your own Cloudflare Pages project with its own database and admin panel — not shared, not hosted by us, always exportable.',
    ctaBrowse: 'Browse templates',
    ctaFeatured: 'Featured',
    featuresTitle: 'What you get',
    features: {
      f1: {
        title: 'Independent deploy',
        body: 'The site runs inside your Cloudflare account: the Pages project and the D1 database belong to you.',
      },
      f2: {
        title: 'Site admin panel',
        body: 'Copy, images, SEO, languages and the form inbox are edited in the admin — no rebuild required.',
      },
      f3: {
        title: 'Multilingual content',
        body: 'Several languages coexist in one site; the language list is driven by the platform locales table.',
      },
      f4: {
        title: 'Upgradable',
        body: 'Template and site backend have separate version lines; upgrades roll out gradually and can be rolled back.',
      },
    },
    prereqTitle: 'Check before deploying (we never manage your account)',
    prereqFree: 'Quota headroom',
    prereqToken: 'Required API token permissions',
    d1Limit: 'D1 databases per account',
    pagesLimit: 'Pages projects per account',
    prereqNote:
      'Deployment uses your token to create the project and database in your account. If the quota is short, the pre-check rejects it — no half-built sites.',
  },
  list: {
    title: 'Templates',
    search: 'Search by name or description',
    searchBtn: 'Search',
    category: 'Category',
    categoryAll: 'All categories',
    tags: 'Tags',
    featuredOnly: 'Featured only',
    clear: 'Clear filters',
    empty: 'No templates match these filters',
    loadMore: 'Load more',
    sites: '{n} sites',
    langs: '{n} languages',
    from: 'from',
    preview: 'Preview',
    detail: 'Details',
  },
  detail: {
    back: 'Back to list',
    version: 'Current version',
    priceTitle: 'Pricing',
    buy: 'Buy & deploy now',
    buyLogin: 'Sign in to buy',
    buyHint: 'Checkout and the deployment wizard land in the next iteration; use the preview to confirm the look now.',
    adminTitle: 'Editable in the site admin ({n} items)',
    adminHint:
      'These come from the template content contract, not a hand-written list — change the contract and this follows.',
    field: 'Field',
    fieldType: 'Type',
    required: 'Required',
    i18nScoped: 'Per language',
    versionsTitle: 'Version history',
    versionStatus: 'Status',
    versionSiteBackend: 'Site backend',
    breaking: 'Breaking change',
    yes: 'Yes',
    no: 'No',
    demoSite: 'Demo site',
    priceMissing: 'Not priced yet (set a price in the admin)',
  },
  preview: {
    title: 'Live preview (real SSR output)',
    desktop: 'Desktop',
    tablet: 'Tablet',
    mobile: 'Mobile',
    openNew: 'Open in new tab',
    loading: 'Loading preview…',
    blocked: 'The preview site did not load',
    blockedHint:
      'A preview project is built when the template repo is tagged and served locally at http://localhost:5175.',
    draftBadge: 'Preview build (watermarked, forms disabled)',
    notBuilt: 'No preview project for this template yet (the template repo has not been tagged)',
  },
  auth: {
    loginTitle: 'Sign in',
    registerTitle: 'Create an account',
    email: 'Email',
    password: 'Password',
    doLogin: 'Sign in',
    doRegister: 'Register and sign in',
    switchToRegister: 'No account? Register',
    switchToLogin: 'Have an account? Sign in',
    loggedInAs: 'Signed in as {email}',
    registerHint: 'Registration is instant locally (the verification token is returned instead of emailed).',
    errBad: 'Wrong email or password ({code})',
  },
  notFound: {
    body: 'This address does not exist on the site. The language prefix or the template slug may be wrong.',
  },
  a7: {
    title: 'Before you buy',
    lede: 'The platform never hosts your Cloudflare account: the site, database and admin all live inside your CF project. Please confirm each item below.',
    needTitle: 'CF resources you must own',
    needCf: 'A Cloudflare account you can sign in to (2FA recommended).',
    needPages: 'Pages quota: 100 projects on the free tier; each license = one Pages project.',
    needD1: 'D1 quota: 10 databases on the free tier; each license = one D1 database.',
    needSecrets: 'Secrets / Variables: written into your Pages project during deploy; we never store them.',
    capacityTitle: 'Suitable traffic band (conservative, per final PoC⑩)',
    capacityBody:
      'This template + Pages + D1 bills about "4 binding calls per page view" (PoC⑩ measured: a single front-end SSR is ≤4 subrequests). On the Cloudflare free tier (~100k Worker requests/day) that sustains roughly ≤25k PV/day of low-concurrency corporate sites; beyond ~25k PV/day you should move to the CF paid plan. High-traffic portals, large e-commerce, CDN origin shielding and multi-region active-active are out of scope — self-host those.',
    licenseTitle: 'Service qualification, not a software license',
    licenseBody:
      'You buy ongoing template content, admin use and upgrade push — not a runtime license. Once delivered, the template files are yours to keep. Suspension only removes updates and support; we never shut your site remotely.',
    dataTitle: 'Data ownership',
    dataBody:
      'Content, form entries and images live in your D1/R2 only. The platform stores your email, order history and license hashes; nothing else.',
  },
  a10: {
    title: 'Platform Terms',
    lede: 'Last updated: 2026-10-23 (v1.0, effective). The baseline of mutual rights and obligations; the capacity statement follows the final PoC⑩ review (see "Before you buy").',
    s1: '1. Nature of service',
    s1Body:
      'The platform delivers templates and deployment assistance; it is not a cloud infrastructure provider. Your site, database and admin all run inside your own Cloudflare account.',
    s2: '2. Account and quota',
    s2Body:
      'Buyers must bring their own Cloudflare account; deploys consume buyer quota. The platform never pays CF on your behalf, and downtime caused by your account exceeding quota or an unpaid balance is not the platform’s liability.',
    s3: '3. Refunds and revocation',
    s3Body:
      'Full refund is available within 7 days of payment provided no site has been successfully deployed. Once deployed, the service qualification is deemed delivered and is non-refundable, though you may stop renewing at any time. Revoking a license only stops future updates and support; we never shut your site remotely and never delete your data.',
    s4: '4. Content ownership',
    s4Body:
      'Buyer-authored text and images belong to the buyer. Template-bundled assets ship with a one-time commercial license.',
    s5: '5. Limitation of liability',
    s5Body:
      'The platform is not liable for CF outages, buyer token leaks, or damage caused by buyer-modified code (including editing _worker.js). Source download is not provided; modified artifacts are unsupported.',
    s6: '6. Upgrades and compatibility',
    s6Body:
      'Major versions are never force-upgraded. Buyers may pin their current version; the platform only ships advisory notices and rollback docs. License expiry affects updates and support only.',
  },
  a9: {
    title: 'Help center',
    lede: 'For buyer operators who are new to Cloudflare: signup, token scopes, admin usage, custom domain, emergency recovery.',
    cfTitle: '1. Cloudflare signup',
    cf1: 'Go to dash.cloudflare.com and register with email + password (Google/GitHub SSO optional).',
    cf2: 'Verify the email and turn on two-factor (authenticator app recommended).',
    cf3: 'Open Account Home after first login and copy the Account ID — the deploy wizard needs it.',
    tokenTitle: '2. API Token with three scopes (least privilege)',
    tokenLede:
      'The platform needs only three permissions and never asks for global write. Create one at: My Profile → API Tokens → Create Token.',
    token1: 'Pages: Edit — create/update Pages projects, upload artifacts, write Secrets/Variables.',
    token2: 'D1: Edit — create/delete databases inside your account, run migrations, read quota.',
    token3:
      'Account Settings: Read — read account-level settings and custom-domain status; DNS records are not written (buyer adds CNAME manually).',
    tokenHint:
      'Tokens are shown once by Cloudflare; the wizard validates them live and never stores the plaintext. B7 “do not retain token” is on by default.',
    conceptsTitle: '3. Pages / D1 / Secrets at a glance',
    conceptsPages: 'Pages = one frontend project + Workers advanced mode; both static output and _worker.js live here.',
    conceptsD1:
      'D1 = Cloudflare’s serverless SQLite. Each Pages project binds exactly one D1 database; all data belongs to the buyer.',
    conceptsSecrets:
      'Secrets/Variables = Pages environment variables. The platform writes LICENSE_KEY, BINDING_ID and PLATFORM_API_BASE.',
    adminTitle: '4. Site admin manual (for buyer operators)',
    adminBody:
      'Login at your site URL + /__admin. The initial password is shown only once in the deploy email — enable a Passkey (Windows Hello / YubiKey / Face ID) right after. Content edits write to your D1 immediately; the next visitor sees them without a rebuild.',
    domainTitle: '5. Custom domain (no DNS-by-proxy)',
    domainBody:
      'The platform only creates the Pages project and its default *.pages.dev hostname. Add your own domain in CF Pages → Custom domains and follow the prompts to insert the CNAME yourself; we never write DNS on your behalf.',
    lockoutTitle: '6. Recovery when all admin accounts are lost',
    lockoutBody:
      'The admin panel lives in your own D1; the platform does not hold admin passwords. Unlock via CF account + D1 CLI running UPDATE site_admins SET locked_until=NULL, failed_attempts=0. Alternatively, ask support to walk you through re-seeding an owner — we’ll verify site ownership by matching the D1 database ID from the original deploy.',
  },
  app: {
    tabOrders: 'My orders',
    tabLicenses: 'My licenses',
    tabDeploy: 'Deploy guide',
    tabSites: 'My sites',
    tabUpdates: 'Available updates',
    needLogin: 'Please sign in first (top-right “Sign in”).',
    ordersTitle: 'My orders',
    ordersEmpty: 'No orders yet.',
    licensesTitle: 'My licenses (service qualification)',
    licensesEmpty: 'No licenses yet.',
    qualificationNote:
      '“Service qualification” only affects future updates and support; the site itself is never shut down remotely.',
    qualifiedActive: 'Qualification: active',
    qualifiedExpiring: 'Qualification: expiring soon',
    qualifiedLapsed: 'Qualification: lapsed (site still online)',
    deployTitle: 'Deploy guide (three steps)',
    deployStep1: 'Create an API Token with only Pages:Edit + D1:Edit + Account Settings:Read.',
    deployStep2: 'Paste the Token; the platform validates it immediately and reports remaining quota.',
    deployStep3:
      'Pick the bind domain and initial content; the platform runs Pages + D1 + Secrets setup inside your CF account.',
    deployHint: 'When pre-checks fail, the wizard translates the error into guidance — never a bare 500.',
    deployHelp: 'Open help center',
    sitesTitle: 'My sites',
    sitesEmpty: 'No deployments yet.',
    sitesAckHint:
      'Initial admin credentials appear only once in the deploy email; this page offers a “I’ve saved them” acknowledgement and never re-shows the password.',
    ackBtn: 'I have saved the initial account',
    ackDone: 'Acknowledged',
    updatesTitle: 'Available updates',
    updatesEmpty: 'No deployments yet.',
    updatesHint: 'Template line and backend line evolve independently. We never force upgrades — only push notices.',
    colId: 'ID',
    colTemplate: 'Template',
    colAmount: 'Amount',
    colStatus: 'Status',
    colCreated: 'Created',
    colSite: 'Site URL',
    colAdmin: 'Admin URL',
    colAckAccount: 'Initial admin',
    colTemplateLine: 'Template line',
    colBackendLine: 'Backend line',

    /* ---- Added: B1/B4/B5/B6/B7/B9/B11/B12 copy ---- */
    tab: {
      profile: 'Profile',
      orders: 'Orders',
      licenses: 'Licenses',
      deploy: 'Bind & deploy',
      sites: 'My sites',
      health: 'Site health',
      logs: 'Activity',
      support: 'Assisted setup',
    },
    profileTitle: 'Account profile',
    email: 'Email',
    emailLocked:
      'Email is the only account-recovery credential; changing it requires re-verifying ownership (online email change is not supported yet).',
    verified: 'Email verified',
    unverified: 'Email not verified (check your inbox)',
    nickname: 'Display name',
    companyName: 'Company (optional)',
    defaultLocale: 'Default language',
    saveProfile: 'Save profile',
    profileSaved: 'Profile saved.',
    passwordTitle: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password (min 10 chars)',
    changePassword: 'Change password',
    passwordChanged: 'Password changed; other devices will be signed out within 15 minutes.',
    passwordHint: 'Changing your password requires the current one — no temporary password by email.',

    bindTitle: 'Bind a Cloudflare account and deploy',
    bindIntro:
      'The platform uses your API Token to create a Pages project and a D1 database inside your own Cloudflare account. The token is only used for deployment — never to read your content.',
    bindLicense: 'License',
    bindFormTitle: 'Submit Cloudflare credentials',
    cfAccountId: 'Cloudflare Account ID',
    cfApiToken: 'API Token',
    tokenRetainedLabel: 'Retain the token for future upgrades (recommended)',
    tokenRetainedHint:
      'If disabled, the encrypted token is physically deleted after a successful deploy and every upgrade requires re-entering it (high-security mode).',
    requiredPerms: 'Required token permissions (three, all mandatory)',
    createToken: 'Open Cloudflare token creation (permissions pre-filled)',
    bindSubmit: 'Validate and bind',
    bindOk: 'Bound successfully.',
    bindGranted: 'Granted permissions:',
    goDeploy: 'Go trigger the first deploy',
    quotaFull: 'This license already uses all of its site quota; add another order to raise it.',
    bindingsTitle: 'Bound sites',
    noBindings: 'No bindings under this license yet.',
    unbind: 'Unbind',
    unbindConfirm:
      'Unbinding stops future upgrades and support for this site; the site itself keeps running in your account. Continue?',
    unbound: 'Unbound.',
    dropToken: 'Drop token',
    retainToken: 'Retain token',
    tokenKept: 'Ciphertext kept (platform can deploy upgrades)',
    tokenDropped: 'Not kept (upgrades need re-authorization)',
    unbindHint:
      'Unbinding does not take your site down or migrate content; the platform does not pay Cloudflare on your behalf and the legacy site is yours to manage.',
    colTokenStatus: 'Token status',
    colDeployStatus: 'Latest deploy',
    colVersion: 'Version (template / backend)',
    colQualification: 'Qualification',
    colExpires: 'Expires',
    colSitesQuota: 'Site quota',
    colLangsQuota: 'Language quota',
    colKind: 'Kind',
    colEvent: 'Event',
    colError: 'Error code',
    colLine: 'Version line',
    colStep: 'Step',
    bindNow: 'Bind / deploy',

    sitesIntro:
      'This list only exposes the site and admin URLs; source code is not downloadable and content is edited in the site admin (/__admin).',
    refresh: 'Refresh',
    deployHistory: 'Deployment history',
    emptyDeploy: 'No deployments yet.',
    claimCreds: 'Claim initial admin password',
    credsTitle: 'Initial site admin account (shown once)',
    credsOnce:
      'It cannot be shown again after closing; the platform stores no plaintext password. Sign in and change it immediately.',
    noDeployment: 'This site has no deployment record yet.',
    upgradeQueued: 'Upgrade queued; this page refreshes the status automatically.',
    updateTemplate: 'Upgrade template line',
    updateBackend: 'Upgrade backend line',
    goHealth: 'View site health',
    goSupport: 'Need assisted setup',

    healthTitle: 'Site health and quota',
    healthIntro:
      'The left column is what the platform can observe directly; the right column is the site’s self-counted quota units reported via heartbeat. Never add them together or convert to PV.',
    noteHeartbeat: 'Heartbeat:',
    noteUnits: 'Quota units:',
    noteThreshold: 'Free-tier limit:',
    liveNow: 'Heartbeat healthy',
    notLive: 'Heartbeat missing',
    platformSide: 'Platform side (directly observable)',
    buyerSide: 'Buyer-reported (self-counted)',
    buyerNoData: 'This site has not reported self-counts yet (appears within 24h after the first deploy).',
    quotaUnits: 'Quota units / daily limit',
    d1ReadWrite: 'D1 rows read / written',
    reportedAt: 'Reported at',
    source: 'Source',
    buyerSelfReported:
      '★ Self-counts may be inaccurate or altered on the site side; the platform makes no accuracy claim.',
    lastHeartbeat: 'Last heartbeat',
    lastDeploy: 'Last deploy',
    warn: {
      binding_inactive: 'Binding is unhealthy; upgrades are unavailable',
      heartbeat_stale: 'No heartbeat for over 36 hours',
      quota_80: 'Quota usage above 80%',
      quota_exceeded: 'Quota exhausted; the site may be down today',
    },

    logsTitle: 'Activity log',
    logsIntro: 'License events and deployment history merged into one timeline for self-service troubleshooting.',
    logsEmpty: 'No records yet.',
    logDeploy: 'Deploy',
    logLicense: 'License',

    supportTitle: 'Assisted binding',
    supportIntro:
      'If the token never validates, open a ticket and our team will help (screenshots or screen sharing). Most failures are a missing permission — check the three items below first.',
    supportKind: 'Issue type',
    supportSeverity: 'Urgency',
    supportContact: 'Contact',
    supportContactHint: 'Email / chat handle / available time',
    supportMessage: 'Description',
    supportSubmit: 'Submit ticket',
    supportSent: 'Ticket submitted; expected first response:',
    supportMine: 'My tickets',
    supportEmpty: 'No tickets yet.',
    supportTokenWarn:
      '★ Never paste an API Token (a long alphanumeric string) into a ticket — messages containing token-like text are rejected. Assisted binding still goes through the encrypted form.',
    noLicense: '(no license)',
    severityShort: 'Priority',
    kind: {
      assist_bind: 'Assisted binding',
      deploy_failure: 'Deploy failure',
      token_permission: 'Token permission',
      other: 'Other',
    },
    sev: { low: 'Low', normal: 'Normal', high: 'High' },
    tstatus: { open: 'Open', in_progress: 'In progress', resolved: 'Resolved', closed: 'Closed' },
  },
  privacy: {
    title: 'Privacy Policy',
    lede: 'Last updated: 2026-10-23 (v1.0, effective). This page explains what we collect, what we never collect, and where your data lives.',
    s1: '1. What we collect',
    s1Body:
      'Email, password hash (PBKDF2, irreversible), order and license records (license keys stored only as SHA-256 hashes), deployment records (status/step/error code) and audit entries for key admin and user actions (IP and request ID). We do not collect ID numbers, phone numbers or card details — card data is handled by the payment provider and never reaches us.',
    s2: '2. What we never collect (v2.0 disclosure)',
    s2Body:
      'Site copy, images, form submissions and site-admin accounts/sessions all live in the D1/R2 of your own Cloudflare account. The platform cannot read them and cannot back them up or restore them; keep your own backups in the Cloudflare dashboard.',
    s3: '3. API tokens and secrets',
    s3Body:
      'Your Cloudflare API Token is stored AES-256-GCM encrypted, with the decryption key held in a Cloudflare Secret rather than in code or the database. With “do not retain token” enabled, the ciphertext is physically deleted once deployment succeeds; the token is never used for anything but deploys.',
    s4: '4. Sub-processors',
    s4Body:
      'Cloudflare (hosting and deployment), the payment provider (Stripe / PayPal) and the email provider (Resend). We share data only as required to deliver those services — never for ad profiling or resale.',
    s5: '5. Retention',
    s5Body:
      'Account data is kept while the account exists. After deletion, personal identifiers are removed within 30 days; order and audit records are retained for 12 months for compliance (amount, timestamp and action type only).',
    s6: '6. Your rights',
    s6Body:
      'Open a ticket from “Assisted setup” in the user center to export or delete your account data. Deletion is irreversible on the platform side; site data in your own account is unaffected.',
  },
  refund: {
    title: 'Refund Policy',
    lede: 'Last updated: 2026-10-23 (v1.0, effective). Consistent with section 3 of the Terms; this page gives the actionable rules and process.',
    s1: '1. Full refund within 7 days if not deployed',
    s1Body:
      'Within 7 days of payment and before the first successful deployment, you may request a full refund without giving a reason.',
    s2: '2. No refund after deployment',
    s2Body:
      'Once the first deployment succeeds the service qualification (template content, site admin, upgrade notices) is deemed delivered and is non-refundable; subscriptions can be cancelled at any time.',
    s3: '3. Exception for failed deployments',
    s3Body:
      'If deployment repeatedly fails for platform reasons (corrupt artifact, orchestration defect) and cannot be fixed, we refund in full. If it fails because of insufficient token permissions, exhausted account quota or an unpaid balance, we help troubleshoot; if it still cannot be delivered, it is treated as undelivered and refunded.',
    s4: '4. Subscriptions and renewals',
    s4Body:
      'Subscriptions bill per period and can be cancelled before the next charge. Started periods are normally non-refundable; unused time may be refunded pro rata after manual review.',
    s5: '5. How to request',
    s5Body:
      'Open a ticket under “Assisted setup”, choose “Other” and include the order ID and reason. We reply within 3 business days; the refund goes back to the original payment method (timing depends on the provider).',
    s6: '6. Revocation is not a refund',
    s6Body:
      'Revocation or expiry only stops future updates and support. It neither triggers a refund nor takes your site offline.',
  },
  common: {
    loading: 'Loading…',
    retry: 'Retry',
    error: 'Request failed',
    errorNetwork: 'Cannot reach the platform API ({base}). Start it with: pnpm api:dev',
    errorLocale: 'That language is not installed: {got} (available: {supported})',
    close: 'Close',
  },
} satisfies typeof zhCN;
